import {
    type SeverityLevel,
    withScope,
    captureException as captureSentryException,
    captureMessage as captureSentryMessage,
    captureEvent,
    getClient,
    getReplay,
    addBreadcrumb as addSentryBreadcrumb,
    type Breadcrumb,
} from '@sentry/browser';
import type {Extras} from '@sentry/core';

import {hasValue} from '@famly/stat_ts-utils_has-value';

/**
 * Utility function used internally to check if Sentry object
 * can capture exceptions and messages properly.
 *
 * @property isSentryUsable
 * @type Ember.ComputedProperty
 */
export const isSentryUsable = () => {
    const isInitialized = Boolean(getClient());

    return isInitialized;
};

const isError = (element: any) => {
    return element instanceof Error;
};

type Data = {
    extra?: Extras;
    fingerprint?: string[];
    level?: SeverityLevel;
    tags?: Record<string, string>;
};

type CaptureExceptionData = Pick<Data, 'extra' | 'level' | 'tags'>;

/**
 * Sends an exception to Sentry if it's initialized.
 *
 * If Sentry hasn't been initialized, we log the error to console.
 *
 * @param error The error to send to Sentry
 * @param data Optional data object
 */
export const captureException = (error: any, data?: CaptureExceptionData) => {
    if (isSentryUsable()) {
        const exception = isError(error) ? error : JSON.stringify(error);
        withScope(scope => {
            if (data && data.extra) {
                const extra = data.extra;

                Object.keys(extra).map(extraKey => {
                    scope.setExtra(extraKey, extra[extraKey]);
                });
            }

            if (data?.tags) {
                const tags = data.tags;

                Object.keys(tags).map(tagKey => {
                    scope.setTag(tagKey, tags[tagKey]);
                });
            }

            scope.setLevel(data && data.level ? data.level : 'error');

            captureSentryException(exception);
        });
    } else {
        console.error('Sentry is not initialized', error);
    }
};

/**
 * Tries to have Sentry capture message, or send it to console.
 *
 * @method captureMessage
 * @param  {String} message The message to capture
 * @param  {Object} data Optional data object
 * @return {Boolean}
 */
export const captureMessage = (message: string, data?: Data): boolean => {
    if (isSentryUsable()) {
        withScope(scope => {
            if (data && data.extra) {
                const extra = data.extra;

                Object.keys(extra).map(extraKey => {
                    scope.setExtra(extraKey, extra[extraKey]);
                });
            }

            if (data?.tags) {
                const tags = data.tags;

                Object.keys(tags).map(tagKey => {
                    scope.setTag(tagKey, tags[tagKey]);
                });
            }

            if (data?.fingerprint) {
                scope.setFingerprint(data.fingerprint);
            }

            scope.setLevel(data && data.level ? data.level : 'error');

            captureSentryMessage(message);
        });
        return true;
    }

    return false;
};

/**
 * Sends an event to Sentry.
 *
 * Note: This doesn't capture a stack trace.
 *
 * @param message The message of the event
 * @param data Optional data object
 */
export const sendEvent = (message: string, data?: Data) => {
    if (!isSentryUsable()) {
        return;
    }

    captureEvent({
        message,
        extra: data?.extra,
        fingerprint: data?.fingerprint,
        level: data?.level,
        tags: data?.tags,
    });
};

/** Starts a manual recording. Returns true if recording started. */
export const startRecording = (): boolean => {
    const replay = getReplay();
    if (!hasValue(replay)) {
        return false;
    }
    // start can throw if there is an ongoing recording already.
    try {
        replay.start();
    } catch {}
    // Sometimes `start` doesn't really start. It seems to help to flush some of the recording.
    replay.flush({continueRecording: true});
    return true;
};

/** Stops a recording. Returns true if recording stopped. */
export const stopRecording = (): Promise<boolean> => {
    const replay = getReplay();
    if (!hasValue(replay)) {
        return Promise.resolve(false);
    }
    return replay.flush().then(() => {
        const replay = getReplay();
        if (!hasValue(replay)) {
            return false;
        }
        replay.stop();
        return true;
    });
};

export const addBreadcrumb = (breadcrumb: Breadcrumb) => {
    if (isSentryUsable()) {
        addSentryBreadcrumb(breadcrumb);
    } else {
        console.warn('Sentry is not initialized, cannot add breadcrumb', breadcrumb);
    }
};
